import { useState, useEffect } from "react";
import { X, Download, ZoomIn, ZoomOut, RotateCcw, FileText, Film, Image as ImageIcon } from "lucide-react";

interface MediaPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  mediaUrl: string;
  mediaType: "image" | "video" | "audio" | "document" | string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
}

export function MediaPreviewModal({
  isOpen,
  onClose,
  mediaUrl,
  mediaType,
  fileName,
  fileSize,
  mimeType,
}: MediaPreviewModalProps) {
  const [zoom, setZoom] = useState(1);

  // Reset zoom whenever modal opens or url changes
  useEffect(() => {
    if (isOpen) {
      setZoom(1);
    }
  }, [isOpen, mediaUrl]);

  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const displayName = fileName || `${mediaType} attachment`;
  const isPdf = mimeType?.includes("pdf") || displayName.toLowerCase().endsWith(".pdf");

  const formatBytes = (bytes?: number) => {
    if (!bytes || bytes <= 0) return "";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm animate-fade-in p-2 sm:p-4">
      {/* Top Header Bar */}
      <div className="absolute top-0 left-0 right-0 h-14 bg-gradient-to-b from-black/80 to-transparent flex items-center justify-between px-4 sm:px-6 z-10">
        <div className="flex items-center gap-3 min-w-0 text-white">
          {mediaType === "image" ? (
            <ImageIcon className="w-5 h-5 text-emerald-400 shrink-0" />
          ) : mediaType === "video" ? (
            <Film className="w-5 h-5 text-purple-400 shrink-0" />
          ) : (
            <FileText className="w-5 h-5 text-blue-400 shrink-0" />
          )}
          <div className="min-w-0">
            <h4 className="text-sm font-semibold truncate max-w-xs sm:max-w-md">{displayName}</h4>
            {fileSize && (
              <span className="text-[11px] text-white/70 font-mono">{formatBytes(fileSize)}</span>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {mediaType === "image" && (
            <div className="flex items-center bg-white/10 rounded-lg p-0.5 mr-2">
              <button
                type="button"
                onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
                className="p-1.5 text-white hover:text-emerald-400 transition-colors"
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setZoom(1)}
                className="p-1.5 text-white hover:text-emerald-400 transition-colors"
                title="Reset Zoom"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setZoom((z) => Math.min(3, z + 0.25))}
                className="p-1.5 text-white hover:text-emerald-400 transition-colors"
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>
          )}

          <a
            href={mediaUrl}
            download={displayName}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow transition-colors"
            title="Download File"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Download</span>
          </a>

          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors ml-1"
            title="Close (ESC)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div
        className="w-full h-full flex items-center justify-center overflow-auto p-4 pt-16 cursor-zoom-out"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        {mediaType === "image" && (
          <div className="relative max-w-full max-h-full flex items-center justify-center cursor-default">
            <img
              src={mediaUrl}
              alt={displayName}
              style={{ transform: `scale(${zoom})`, transition: "transform 0.15s ease-out" }}
              className="max-w-[90vw] max-h-[85vh] object-contain rounded shadow-2xl select-none"
            />
          </div>
        )}

        {mediaType === "video" && (
          <div className="relative max-w-[90vw] max-h-[85vh] rounded-xl overflow-hidden shadow-2xl bg-black cursor-default">
            <video
              src={mediaUrl}
              controls
              autoPlay
              playsInline
              className="max-w-full max-h-[80vh] rounded-lg"
            />
          </div>
        )}

        {mediaType === "document" && isPdf && (
          <div className="w-[90vw] max-w-4xl h-[80vh] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col cursor-default">
            <iframe
              src={`${mediaUrl}#toolbar=1`}
              title={displayName}
              className="w-full flex-1 border-none"
            />
          </div>
        )}

        {mediaType === "document" && !isPdf && (
          <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center space-y-4 shadow-2xl cursor-default">
            <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto shadow-inner">
              <FileText className="w-8 h-8" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900 truncate">{displayName}</h3>
              {fileSize && (
                <p className="text-xs text-slate-500 mt-1 font-mono">{formatBytes(fileSize)}</p>
              )}
            </div>
            <p className="text-xs text-slate-600">
              This document cannot be previewed directly in browser. Download to view it on your device.
            </p>
            <a
              href={mediaUrl}
              download={displayName}
              className="inline-flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs transition-colors shadow"
            >
              <Download className="w-4 h-4" />
              <span>Download File</span>
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
